import React from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { GitBranch, Plus, Trash2, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import AutomationActionParamsEditor from './AutomationActionParamsEditor';
import CategoryAutocompleteInput, { type CategoryOption } from './CategoryAutocompleteInput';
import {
  ACTION_TYPES,
  CONDITION_FIELDS,
  TICKET_CONDITION_FIELDS,
  OPERATORS,
  EVENT_LABELS,
} from './automationConstants';
import { NODE_W } from './automationFlowLayout';

/** Shared callback/context bag attached identically to every node's `data`
 * by AutomationFlowEditor.tsx — see that file for what each does. */
export interface FlowSharedActions {
  isTicketEvent: boolean;
  agents: any[];
  teams: any[];
  labels: any[];
  categories: CategoryOption[];
  templates: Array<{ id: number; name: string; language?: string }>;
  channels: Array<{ id: number; name: string }>;
  onChangeActionType: (id: string, actionType: string) => void;
  onChangeActionParam: (id: string, key: string, value: any) => void;
  onDeleteAction: (id: string, isBranch: boolean) => void;
  onConvertToBranch: (id: string) => void;
  onInsertActionAfter: (id: string) => void;
  onChangeBranchField: (branchId: string, field: string) => void;
  onAddCase: (branchId: string) => void;
  onRemoveCase: (branchId: string, caseId: string) => void;
  onUpdateCase: (branchId: string, caseId: string, patch: Record<string, any>) => void;
  onAddActionTo: (branchId: string | null, caseId: string | null) => void;
}

/** Callback bag for the Conditions node — separate from FlowSharedActions
 * since it edits `conditions` (rule-level state in AutomationRuleDialog),
 * not the `actions` tree. */
export interface FlowConditionActions {
  isTicketEvent: boolean;
  categories: CategoryOption[];
  onAddCondition: () => void;
  onRemoveCondition: (index: number) => void;
  onChangeCondition: (index: number, field: string, value: any) => void;
}

// nodrag/nopan: without these, React Flow's own pointer handling on a node
// (drag-start detection, pane panning) can intercept clicks before they
// reach a Button/Select/Input inside it — especially with Radix's Select,
// which does its own pointer-capture/portal handling. This is what made
// every control inside a node feel unresponsive; nodesDraggable={false}
// alone (set on the canvas) does not exempt inner elements from this.
const cardBase =
  'nodrag nopan rounded-lg border border-border bg-card text-card-foreground shadow-sm';

export const ConditionFlowNode: React.FC<NodeProps> = ({ data }) => {
  const conditions = ((data as any).conditions || []) as any[];
  const actions = (data as any).conditionsActions as FlowConditionActions;
  const fields = actions.isTicketEvent ? TICKET_CONDITION_FIELDS : CONDITION_FIELDS;
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} p-3`}>
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Conditions
        </span>
        <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={actions.onAddCondition}>
          <Plus className="h-3 w-3" /> Add
        </Button>
      </div>
      <div className="mt-2 space-y-2">
        {conditions.map((condition, index) => (
          <div key={index} className="space-y-1.5 rounded-md border border-border/60 bg-muted/10 p-2">
            {index > 0 && (
              <Select
                value={condition.group_operator || 'AND'}
                onValueChange={(v) => actions.onChangeCondition(index, 'group_operator', v)}
              >
                <SelectTrigger className="h-6 w-[76px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">AND</SelectItem>
                  <SelectItem value="OR">OR</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={condition.field_name} onValueChange={(v) => actions.onChangeCondition(index, 'field_name', v)}>
              <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Field" /></SelectTrigger>
              <SelectContent>
                {fields.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={condition.operator} onValueChange={(v) => actions.onChangeCondition(index, 'operator', v)}>
              <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Operator" /></SelectTrigger>
              <SelectContent>
                {OPERATORS.map((op) => <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              {condition.field_name === 'category_id' ? (
                <CategoryAutocompleteInput
                  className="h-7 flex-1 text-xs"
                  value={condition.expected_value || ''}
                  onChange={(v) => actions.onChangeCondition(index, 'expected_value', v)}
                  categories={actions.categories}
                />
              ) : (
                <Input
                  className="h-7 flex-1 text-xs"
                  value={condition.expected_value || ''}
                  onChange={(e) => actions.onChangeCondition(index, 'expected_value', e.target.value)}
                  placeholder="Value…"
                />
              )}
              <Button
                type="button" variant="ghost" size="sm"
                className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => actions.onRemoveCondition(index)}
                disabled={conditions.length === 1}
                aria-label="Remove condition"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const TriggerFlowNode: React.FC<NodeProps> = ({ data }) => {
  const triggerEvent = (data as any).triggerEvent as string;
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} border-primary/40 bg-primary/5 px-3 py-2.5`}>
      <div className="flex items-center gap-2 text-sm font-medium">
        <Zap className="h-4 w-4 text-primary" />
        Trigger
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {EVENT_LABELS[triggerEvent] || triggerEvent}
      </p>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const ActionFlowNode: React.FC<NodeProps> = ({ id, data }) => {
  const node = (data as any).node;
  const actions = (data as any).actions as FlowSharedActions;
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} p-3`}>
      <Handle type="target" position={Position.Top} />
      <div className="flex items-start justify-between gap-1">
        <Select value={node.action_type} onValueChange={(v) => actions.onChangeActionType(id, v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ACTION_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-2">
        <AutomationActionParamsEditor
          actionType={node.action_type}
          params={node.action_parameters || {}}
          onChange={(key, value) => actions.onChangeActionParam(id, key, value)}
          isTicketEvent={actions.isTicketEvent}
          agents={actions.agents}
          teams={actions.teams}
          labels={actions.labels}
          templates={actions.templates}
          channels={actions.channels}
          idPrefix={`flow-${id}`}
        />
      </div>
      <div className="mt-2.5 flex items-center gap-1.5 border-t border-border/60 pt-2">
        <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={() => actions.onInsertActionAfter(id)}>
          <Plus className="h-3 w-3" /> Action
        </Button>
        <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={() => actions.onConvertToBranch(id)}>
          <GitBranch className="h-3 w-3" /> Branch
        </Button>
        <Button
          type="button" variant="ghost" size="sm"
          className="ml-auto h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => actions.onDeleteAction(id, false)}
          aria-label="Delete action"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const BranchFlowNode: React.FC<NodeProps> = ({ id, data }) => {
  const node = (data as any).node;
  const actions = (data as any).actions as FlowSharedActions;
  const params = node.action_parameters || {};
  const fields = actions.isTicketEvent ? TICKET_CONDITION_FIELDS : CONDITION_FIELDS;
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} border-amber-400/60 bg-amber-50/60 p-3 dark:bg-amber-950/20`}>
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
        <GitBranch className="h-3.5 w-3.5" /> Branch on
      </div>
      <div className="mt-2">
        <Select value={params.field || ''} onValueChange={(v) => actions.onChangeBranchField(id, v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select field" /></SelectTrigger>
          <SelectContent>
            {fields.map((f) => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-2.5 flex items-center gap-1.5 border-t border-amber-400/40 pt-2">
        <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={() => actions.onAddCase(id)}>
          <Plus className="h-3 w-3" /> Case
        </Button>
        <Button
          type="button" variant="ghost" size="sm"
          className="ml-auto h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => actions.onDeleteAction(id, true)}
          aria-label="Delete branch"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const CaseFlowNode: React.FC<NodeProps> = ({ data }) => {
  const { branchId, caseId, isDefault, operator, expected_value, field, actions } = data as any as {
    branchId: string; caseId: string; isDefault: boolean; operator?: string; expected_value?: any; field?: string;
    actions: FlowSharedActions;
  };
  if (isDefault) {
    return (
      <div style={{ width: NODE_W }} className={`${cardBase} border-dashed bg-muted/30 px-3 py-2`}>
        <Handle type="target" position={Position.Top} />
        <p className="text-xs font-medium text-muted-foreground">Else (no case matched)</p>
        <Handle type="source" position={Position.Bottom} />
      </div>
    );
  }
  return (
    <div style={{ width: NODE_W }} className={`${cardBase} px-3 py-2`}>
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center gap-1.5">
        <Select
          value={operator || 'equal_to'}
          onValueChange={(v) => actions.onUpdateCase(branchId, caseId, { operator: v })}
        >
          <SelectTrigger className="h-7 flex-1 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {OPERATORS.map((op) => (
              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button" variant="ghost" size="sm"
          className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => actions.onRemoveCase(branchId, caseId)}
          aria-label="Remove case"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {field === 'category_id' ? (
        <CategoryAutocompleteInput
          className="mt-1.5 h-7 text-xs"
          value={expected_value ?? ''}
          onChange={(v) => actions.onUpdateCase(branchId, caseId, { expected_value: v })}
          categories={actions.categories}
        />
      ) : (
        <Input
          className="mt-1.5 h-7 text-xs"
          value={expected_value ?? ''}
          onChange={(e) => actions.onUpdateCase(branchId, caseId, { expected_value: e.target.value })}
          placeholder="Value to compare…"
        />
      )}
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const AddActionFlowNode: React.FC<NodeProps> = ({ data }) => {
  const { branchId, caseId, actions } = data as any as {
    branchId: string | null; caseId: string | null; actions: FlowSharedActions;
  };
  return (
    <div style={{ width: NODE_W }} className="nodrag nopan">
      <Handle type="target" position={Position.Top} />
      <button
        type="button"
        onClick={() => actions.onAddActionTo(branchId, caseId)}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
      >
        <Plus className="h-3.5 w-3.5" /> Add action
      </button>
    </div>
  );
};

export const automationFlowNodeTypes = {
  trigger: TriggerFlowNode,
  condition: ConditionFlowNode,
  action: ActionFlowNode,
  branch: BranchFlowNode,
  case: CaseFlowNode,
  addAction: AddActionFlowNode,
};
